// The board's loading, error, and data state. Like ../../useApi, with one
// difference: a refresh after recording a campaign keeps the board on screen
// while the new one loads, so the form and its success message stay put
// instead of the page dropping back to its loading state.
import { useCallback, useEffect, useState } from "react";
import type { BenchmarkBoard } from "@dashboard-intelligent/api/src/modules/benchmark/types";
import type { IndustrySlug } from "@dashboard-intelligent/api/src/modules/industry/types";
import type { ApiState } from "../../useApi";
import { benchmarkApi } from "./api";

export function useBoard(slug: IndustrySlug): ApiState<BenchmarkBoard> & { reload: () => void; refresh: () => void } {
  const [state, setState] = useState<ApiState<BenchmarkBoard>>({ status: "loading", data: null, error: null });
  const [request, setRequest] = useState({ n: 0, quiet: false });
  useEffect(() => {
    let current = true;
    if (!request.quiet) setState({ status: "loading", data: null, error: null });
    benchmarkApi.board(slug).then(
      (data) => current && setState({ status: "ready", data, error: null }),
      (err: unknown) => {
        if (!current) return;
        // A quiet refresh that fails keeps the board already shown.
        if (request.quiet) return;
        setState({ status: "error", data: null, error: err instanceof Error ? err.message : "Tidak dapat menghubungi server." });
      },
    );
    return () => {
      current = false;
    };
  }, [slug, request]);

  const reload = useCallback(() => setRequest((r) => ({ n: r.n + 1, quiet: false })), []);
  const refresh = useCallback(() => setRequest((r) => ({ n: r.n + 1, quiet: true })), []);
  return { ...state, reload, refresh };
}
